// Storage Helpers Service - Unified Storage Mechanism Interfaces
// Extracted from userDataPersistence.js for better separation of concerns

import { debugLog } from '../debugUtils';
import { getStorageAvailability } from './storageDetection';

// Storage keys and configuration
const STORAGE_PREFIX = 'swts_'; // Sing with the Stars prefix
export const USER_DATA_KEY = `${STORAGE_PREFIX}user_data`;

// Cookie expiry (90 days for reasonable persistence)
const COOKIE_EXPIRY_MS = 90 * 24 * 60 * 60 * 1000; // 90 days in milliseconds

// In-memory storage fallback (runtime only)
const inMemoryStorage = {};

/**
 * localStorage helpers - uses natural lifespan (until user clears browser data)
 */
export const localStorageHelpers = {
  get: key => {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  set: (key, value) => {
    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch (e) {
      return false;
    }
  },
  remove: key => {
    try {
      window.localStorage.removeItem(key);
      return true;
    } catch (e) {
      return false;
    }
  },
};

/**
 * sessionStorage helpers - uses natural lifespan (until tab closes)
 */
export const sessionStorageHelpers = {
  get: key => {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  set: (key, value) => {
    try {
      window.sessionStorage.setItem(key, value);
      return true;
    } catch (e) {
      return false;
    }
  },
  remove: key => {
    try {
      window.sessionStorage.removeItem(key);
      return true;
    } catch (e) {
      return false;
    }
  },
};

/**
 * Cookie helpers - 90 day expiry for reasonable persistence
 * SPECIAL HANDLING: Facebook attribution parameters (fbc/fbp) are stored without URL encoding
 * to prevent corruption of Facebook Click IDs which can contain special characters
 */
export const cookieHelpers = {
  get: key => {
    try {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${key}=`);
      if (parts.length === 2) {
        const cookieValue = parts.pop().split(';').shift();

        // Special handling for Facebook attribution parameters - don't decode
        if (key.includes('fbc') || key.includes('fbp')) {
          debugLog(
            `🔗 Retrieved Facebook attribution parameter without URL decoding:`,
            { key, hasValue: !!cookieValue }
          );
          return cookieValue; // Return raw value to preserve Facebook Click ID integrity
        }

        return decodeURIComponent(cookieValue);
      }
    } catch (e) {
      // Cookie access failed
    }
    return null;
  },
  set: (key, value) => {
    try {
      const expires = new Date(Date.now() + COOKIE_EXPIRY_MS).toUTCString();

      // Special handling for Facebook attribution parameters - don't encode
      if (key.includes('fbc') || key.includes('fbp')) {
        // Store Facebook attribution values without URL encoding to preserve Click ID integrity
        debugLog(
          `🔗 Storing Facebook attribution parameter without URL encoding:`,
          { key, valueLength: value?.length }
        );
        document.cookie = `${key}=${value}; expires=${expires}; path=/; Secure; SameSite=Lax`;
      } else {
        document.cookie = `${key}=${encodeURIComponent(value)}; expires=${expires}; path=/; Secure; SameSite=Lax`;
      }
      return true;
    } catch (e) {
      return false;
    }
  },
  remove: key => {
    try {
      document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Secure; SameSite=Lax`;
      return true;
    } catch (e) {
      return false;
    }
  },
};

/**
 * URL parameter helpers - READ-ONLY (never written to)
 */
export const urlParamHelpers = {
  get: (key, searchParams = null) => {
    try {
      // Use provided searchParams or default to window.location.search
      const searchString =
        searchParams !== null
          ? searchParams
          : typeof window !== 'undefined'
            ? window.location.search
            : '';
      const params = new URLSearchParams(searchString);
      return params.get(key);
    } catch (e) {
      return null;
    }
  },
  // URL params are ALWAYS read-only
  set: () => false,
  remove: () => false,
};

/**
 * In-memory storage helpers - uses natural lifespan (until page refresh)
 */
export const inMemoryHelpers = {
  get: key => {
    return inMemoryStorage[key] || null;
  },
  set: (key, value) => {
    inMemoryStorage[key] = value;
    return true;
  },
  remove: key => {
    delete inMemoryStorage[key];
    return true;
  },
};

/**
 * Read data from storage mechanisms in priority order
 */
export const readFromStorage = key => {
  const availability = getStorageAvailability();

  // Try each storage mechanism in priority order (URL params excluded from writes)
  const mechanisms = [
    {
      name: 'localStorage',
      available: availability.localStorage,
      helper: localStorageHelpers,
    },
    {
      name: 'sessionStorage',
      available: availability.sessionStorage,
      helper: sessionStorageHelpers,
    },
    { name: 'cookies', available: availability.cookies, helper: cookieHelpers },
    {
      name: 'urlParams',
      available: availability.urlParams,
      helper: urlParamHelpers,
    },
    {
      name: 'inMemory',
      available: availability.inMemory,
      helper: inMemoryHelpers,
    },
  ];

  for (const mechanism of mechanisms) {
    if (!mechanism.available) continue;

    const value = mechanism.helper.get(key);
    if (value !== null) {
      debugLog(`📖 Retrieved data from ${mechanism.name}:`, { key });
      return { value, source: mechanism.name };
    }
  }
  return { value: null, source: null };
};

/**
 * Write data to all available storage mechanisms (except URL params which are read-only)
 */
export const writeToStorage = (key, value) => {
  const availability = getStorageAvailability();
  const results = {};

  // Write to all available writable mechanisms
  if (availability.localStorage) {
    results.localStorage = localStorageHelpers.set(key, value);
  }

  if (availability.sessionStorage) {
    results.sessionStorage = sessionStorageHelpers.set(key, value);
  }

  if (availability.cookies) {
    // Limit cookie size (4KB limit, leave room for other cookies)
    if (value.length < 3000) {
      results.cookies = cookieHelpers.set(key, value);
    } else {
      results.cookies = false; // Skip cookies for large data
    }
  }

  // Always write to in-memory as final fallback
  results.inMemory = inMemoryHelpers.set(key, value);

  const successCount = Object.values(results).filter(Boolean).length;
  debugLog(`💾 Wrote to ${successCount} storage mechanisms:`, { key, results });

  return results;
};

/**
 * Remove data from all storage mechanisms
 */
export const removeFromStorage = key => {
  const availability = getStorageAvailability();
  const results = {};

  if (availability.localStorage) {
    results.localStorage = localStorageHelpers.remove(key);
  }

  if (availability.sessionStorage) {
    results.sessionStorage = sessionStorageHelpers.remove(key);
  }

  if (availability.cookies) {
    results.cookies = cookieHelpers.remove(key);
  }

  results.inMemory = inMemoryHelpers.remove(key);

  const successCount = Object.values(results).filter(Boolean).length;
  debugLog(`🗑️ Removed from ${successCount} storage mechanisms:`, {
    key,
    results,
  });

  return results;
};
